/**
 * ═══════════════════════════════════════════════════════════════════════════
 * URLS IN TEXT SOMEBODY TYPED
 *
 * Announcement bodies are plain text, and the most useful thing anyone puts in
 * one is a link — a Meet room, a form, a policy document. Rendered as text it
 * has to be selected and copied, which on a phone is most of a minute and on a
 * truncated card is impossible.
 *
 * The text is authored by a person, so it is untrusted input that ends up in an
 * `href`. Two rules make that safe, and both are enforced here rather than at
 * the call site:
 *
 *   Only http and https become links. `javascript:`, `data:` and `vbscript:`
 *   are exactly the schemes an attacker would use, and there is no legitimate
 *   announcement that needs one — so they are left as plain text.
 *
 *   The href is what was matched, never a reconstruction. A link whose visible
 *   text and destination disagree is the shape of every phishing link.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type TextSegment =
  | { kind: 'text'; value: string }
  | { kind: 'link'; value: string; href: string };

/**
 * Candidates: an explicit scheme, or a bare `www.` host.
 *
 * Deliberately stops at whitespace and at the quote and bracket characters
 * that normally surround a URL rather than belonging to it. Angle brackets are
 * excluded so a pasted `<https://…>` does not carry them into the href.
 */
const CANDIDATE = /(?:https?:\/\/|www\.)[^\s<>"'`]+/gi;

/**
 * Punctuation that ends a sentence rather than a URL.
 *
 * "See https://example.com/page." is a link followed by a full stop, and
 * including the stop gives a 404. Brackets are handled separately, because a
 * URL may legitimately contain balanced ones.
 */
const TRAILING = /[.,;:!?]+$/;

const occurrences = (haystack: string, needle: string) => haystack.split(needle).length - 1;

/** Trims sentence punctuation and any closing bracket with no opener inside. */
function trimTrailing(raw: string): string {
  let value = raw.replace(TRAILING, '');

  /**
   * "(see https://example.com/x)" — the final paren closes the sentence, not
   * the URL. A URL carrying balanced parens of its own keeps them, which is
   * what makes a Wikipedia link survive.
   */
  for (const [open, close] of [['(', ')'], ['[', ']'], ['{', '}']] as const) {
    while (value.endsWith(close) && occurrences(value, close) > occurrences(value, open)) {
      value = value.slice(0, -1);
    }
  }

  return value.replace(TRAILING, '');
}

/**
 * The destination for a matched candidate, or null if it must stay as text.
 *
 * `new URL` is the parser, not a regex: it is the same one the browser will
 * use for the href, so what is checked here is what will actually be
 * navigated to.
 */
function hrefFor(value: string): string | null {
  const candidate = /^www\./i.test(value) ? `https://${value}` : value;

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  // A scheme with no host — "https://" alone, or "http:///path".
  if (!url.hostname) return null;

  return url.href;
}

/**
 * Splits text into plain runs and links, in order.
 *
 * Returns a single text segment when there is nothing to link, so callers can
 * render the result unconditionally.
 */
export function linkify(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let cursor = 0;

  // Fresh regex each call: /g carries `lastIndex` between calls otherwise, and
  // a shared one would skip matches on every second invocation.
  const pattern = new RegExp(CANDIDATE.source, 'gi');

  for (let match = pattern.exec(text); match !== null; match = pattern.exec(text)) {
    const raw = match[0];
    const value = trimTrailing(raw);
    const href = value ? hrefFor(value) : null;

    if (!href) continue;

    if (match.index > cursor) {
      segments.push({ kind: 'text', value: text.slice(cursor, match.index) });
    }

    segments.push({ kind: 'link', value, href });
    cursor = match.index + value.length;
  }

  if (cursor < text.length) {
    segments.push({ kind: 'text', value: text.slice(cursor) });
  }

  return segments;
}

/** Whether the text carries anything that would become a link. */
export function hasLink(text: string): boolean {
  return linkify(text).some((s) => s.kind === 'link');
}

/**
 * The first link in the text, for somewhere that cannot show all of it.
 *
 * A dashboard card clamps the body to two lines, and the meeting link is
 * usually at the end — so linkifying the body alone leaves the one thing
 * somebody needs sitting in the hidden third line.
 */
export function firstLink(text: string): { value: string; href: string } | null {
  const found = linkify(text).find((s) => s.kind === 'link');
  return found?.kind === 'link' ? { value: found.value, href: found.href } : null;
}

/**
 * The text with one link taken out of it.
 *
 * For a card that shows the link separately — a chip, a button — where
 * leaving the raw URL in the sentence prints the same destination twice: a
 * long one you cannot read and a short one you can. Two controls for one
 * destination is one too many, and the chip is the better of the two.
 *
 * What is left is tidied rather than merely cut. Removing the URL from
 * "Standup — https://meet.google.com/x" leaves "Standup —", and a heading
 * ending in a dangling dash reads as text that failed to load. Leading and
 * trailing separators go with it.
 *
 * Returns an empty string when the text was nothing but the link, which is
 * the common case: the caller then draws no heading at all and lets the chip
 * be the whole of it.
 */
export function withoutLink(text: string, link: string): string {
  const at = text.indexOf(link);
  const rest = at === -1 ? text : text.slice(0, at) + text.slice(at + link.length);

  return rest
    // A title is one line however it was typed.
    .replace(/\s+/g, ' ')
    // Separators that were joining the link to the rest, now joining nothing.
    .replace(/^[\s–—\-·:|,]+/, '')
    .replace(/[\s–—\-·:|,]+$/, '')
    .trim();
}

/**
 * A URL shortened for a narrow chip: host plus a little path.
 *
 * The host is never truncated — it is the part that tells somebody where they
 * are about to go, and a link showing an abbreviated domain is worse than one
 * showing none.
 */
export function shortenUrl(href: string, maxLength = 34): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }

  const host = url.hostname.replace(/^www\./, '');
  const rest = `${url.pathname === '/' ? '' : url.pathname}${url.search}${url.hash}`;

  if (host.length + rest.length <= maxLength) return `${host}${rest}`;

  const room = Math.max(0, maxLength - host.length - 1);
  return room <= 1 ? host : `${host}${rest.slice(0, room)}…`;
}
